import { lastPageFor } from "../searchCriteria";

interface iPagerProps {
    /**
     * Trusted to be within `1..lastPageFor(totalCount, itemsPerPage)`.
     *
     * Nothing here clamps it — `clampPage` is the caller's job, and doing it
     * twice would let the two disagree. Hand this a page past the end and it
     * says so honestly ("Page 99 of 3") with Next still enabled, which is the
     * right failure for a presentational component: visible, not papered over.
     */
    page: number;
    itemsPerPage: number;
    /** The size of the full filtered set, not of this page. */
    totalCount: number;
    onPage: (page: number) => void;
}

function Pager({ page, itemsPerPage, totalCount, onPage }: iPagerProps) {
    const lastPage = lastPageFor(totalCount, itemsPerPage);

    return (
        <div className="mp-pager">
            {/*
                totalCount is a required measurement, not an optional one — 0
                means "nothing matched" and is shown as the digit, same as
                every other count. aria-live announces it politely so an
                operator who narrows a filter hears the new total without
                having to find it visually; aria-atomic re-reads the whole
                phrase rather than a partial diff.
            */}
            <span
                className="mp-pager-count"
                aria-live="polite"
                aria-atomic="true"
            >
                {totalCount.toLocaleString()} {totalCount === 1 ? "email" : "emails"} matched
            </span>
            {/*
                Also announced. The implementer's first instinct was that this
                changes only when the operator clicks Previous or Next, making
                an announcement redundant — but it is not the only way the page
                moves. A filter that narrows the result set makes the current
                page unreachable, and the hook clamps it, so an operator can be
                moved to a different page without touching this control at all.
                Being moved silently is the more surprising of the two facts.
            */}
            <span
                className="mp-pager-page"
                aria-live="polite"
                aria-atomic="true"
            >
                Page {page} of {lastPage}
            </span>
            <button
                type="button"
                className="g-btn btn-default"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => onPage(page - 1)}
            >
                Previous
            </button>
            <button
                type="button"
                className="g-btn btn-default"
                aria-label="Next page"
                disabled={page >= lastPage}
                onClick={() => onPage(page + 1)}
            >
                Next
            </button>
        </div>
    );
}

export default Pager;
